import { Injectable, inject } from '@angular/core';
import { Observable, first, map } from 'rxjs';
import { ConfigurationsService, OciPunchoutHandler, PunchoutOrderRow } from 'gung-standard';

const UNIT_CODE_CONFIG_ID = 'VieriPunchoutUnitCodeMappingConfig';

// OCI mapper for the Nor Tekstil punchout (Vieri Bestilling), bound via the punchout config's qualifier
@Injectable({ providedIn: 'root' })
export class OttoOlsenOciPunchoutHandler extends OciPunchoutHandler {
  override readonly identifier = 'otto-olsen-oci';
  override readonly label = 'Otto Olsen (OCI)';

  protected configurationsService = inject(ConfigurationsService);

  // upper-cased enhetskod -> the code Vieri expects; empty until prepare() has run
  protected unitCodes: { [s: string]: string } = {};

  override prepare(): Observable<void> {
    return this.configurationsService.getConfigurationsByIds([UNIT_CODE_CONFIG_ID]).pipe(
      first(),
      map(configs => {
        const configured = configs?.find(c => c?.id === UNIT_CODE_CONFIG_ID)?.unitCodes || {};
        this.unitCodes = Object.keys(configured).reduce(
          (acc, key) => {
            acc[key.trim().toUpperCase()] = String(configured[key]).trim();
            return acc;
          },
          {} as { [s: string]: string }
        );
      })
    );
  }

  protected override mapRow(
    row: PunchoutOrderRow,
    index: number,
    extras: { punchoutOrderId?: string }
  ): { [s: string]: string } {
    const fields = super.mapRow(row, index, extras);
    const i = index + this.indexBase;

    const unit = this.resolveUnit(row);
    if (unit) {
      fields[`NEW_ITEM-UNIT[${i}]`] = unit;
    }
    const unspsc = this.resolveUnspsc(row);
    if (unspsc) {
      fields[`NEW_ITEM-MATGROUP[${i}]`] = unspsc;
    }
    return fields;
  }

  // Otto sells by metre and in packs, so the standard hardcoded 'EA' would make Vieri order the wrong quantity
  protected resolveUnit(row: PunchoutOrderRow): string {
    const enhetskod = row.product?.extra?.ar?.enhetskod;
    if (enhetskod === undefined || enhetskod === null || enhetskod === '') {
      return '';
    }
    const raw = String(enhetskod).trim();
    // an unmapped code goes out raw rather than as 'EA' — a wrong unit is worse than one Vieri rejects
    return this.unitCodes[raw.toUpperCase()] || raw;
  }

  // the column is ar.q_jis_unspscid; ar.q_jis_unspsc is only the q_gung_CreateItem parameter name #gmsIgbBi
  protected resolveUnspsc(row: PunchoutOrderRow): string {
    const unspsc = row.product?.extra?.ar?.q_jis_unspscid;
    if (unspsc === undefined || unspsc === null) {
      return '';
    }
    return String(unspsc).trim();
  }
}
